import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FiUpload, FiCpu, FiFilter, FiDownload, FiCheck, FiLoader, FiChevronRight } from 'react-icons/fi';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const WorkflowContainer = styled.div`
  padding: 30px 20px;
  overflow-x: auto;
`;

const ModeDescription = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 15px 20px;
  margin-bottom: 25px;
  border-radius: 8px;
  
  strong {
    color: #1976d2;
    font-size: 0.95rem;
  }
`;

const StepsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: max-content;
  margin-bottom: 30px;
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StepCard = styled.div`
  width: 180px;
  background: ${props => {
    if (props.$completed) return 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)';
    if (props.$loading) return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
    if (props.$active) return '#ffffff';
    return '#fafafa';
  }};
  border: 3px solid ${props => {
    if (props.$completed) return '#4caf50';
    if (props.$loading) return '#4a90e2';
    if (props.$active) return '#4a90e2';
    return '#e0e0e0';
  }};
  border-radius: 16px;
  padding: 20px;
  transition: all 0.3s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  box-shadow: ${props => {
    if (props.$completed) return '0 4px 20px rgba(76, 175, 80, 0.3)';
    if (props.$loading) return '0 4px 20px rgba(74, 144, 226, 0.3)';
    if (props.$active) return '0 4px 20px rgba(74, 144, 226, 0.2)';
    return '0 2px 8px rgba(0, 0, 0, 0.08)';
  }};
  
  &:hover {
    transform: ${props => props.$clickable ? 'translateY(-4px)' : 'none'};
    box-shadow: ${props => props.$clickable ? '0 6px 24px rgba(74, 144, 226, 0.4)' : props.$completed ? '0 4px 20px rgba(76, 175, 80, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  }
`;

const StepIconContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  position: relative;
  transition: all 0.4s ease;
  
  background: ${props => {
    if (props.$completed) return 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)';
    if (props.$loading) return 'linear-gradient(135deg, #4a90e2 0%, #5c6bc0 100%)';
    if (props.$active) return 'linear-gradient(135deg, #64b5f6 0%, #90caf9 100%)';
    return '#f5f5f5';
  }};
  
  color: ${props => {
    if (props.$completed || props.$loading) return '#ffffff';
    if (props.$active) return '#ffffff';
    return '#bdbdbd';
  }};
  
  border: 4px solid ${props => {
    if (props.$completed) return '#4caf50';
    if (props.$loading) return '#4a90e2';
    if (props.$active) return '#64b5f6';
    return '#e0e0e0';
  }};
  
  box-shadow: ${props => {
    if (props.$completed) return '0 3px 15px rgba(76, 175, 80, 0.5)';
    if (props.$loading) return '0 3px 15px rgba(74, 144, 226, 0.5)';
    return 'none';
  }};
  
  ${props => props.$loading && css`
    svg {
      animation: ${spin} 1.5s linear infinite;
    }
  `}
`;

const StepNumber = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${props => {
    if (props.$completed) return '#4caf50';
    if (props.$loading) return '#4a90e2';
    return '#9e9e9e';
  }};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const StepTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: center;
  color: ${props => {
    if (props.$completed) return '#2e7d32';
    if (props.$loading || props.$active) return '#1976d2';
    return '#757575';
  }};
`;

const StepDescription = styled.p`
  margin: 0 0 12px 0;
  font-size: 0.75rem;
  text-align: center;
  color: ${props => {
    if (props.$completed) return '#388e3c';
    if (props.$loading || props.$active) return '#1976d2';
    return '#9e9e9e';
  }};
  line-height: 1.4;
  min-height: 35px;
`;

const StepStatus = styled.div`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: center;
  
  background: ${props => {
    if (props.$status === 'completed') return '#4caf50';
    if (props.$status === 'loading') return '#4a90e2';
    if (props.$status === 'active') return '#ff9800';
    return '#e0e0e0';
  }};
  
  color: ${props => {
    if (props.$status === 'pending') return '#757575';
    return '#ffffff';
  }};
`;

const Connector = styled.div`
  width: 50px;
  height: 4px;
  background: ${props => {
    if (props.$completed) return 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)';
    if (props.$active) return 'linear-gradient(90deg, #4a90e2 0%, #64b5f6 100%)';
    return '#e0e0e0';
  }};
  position: relative;
  transition: all 0.3s ease;
  
  ${props => props.$active && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
  
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConnectorIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.$active ? '#4a90e2' : '#e0e0e0'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const DownloadSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const BigDownloadButton = styled.button`
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: white;
  border: none;
  padding: 16px 50px;
  font-size: 1.1rem;
  font-weight: 700;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(76, 175, 80, 0.5);
  }
  
  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
    box-shadow: none;
  }
  
  svg {
    font-size: 1.3rem;
  }
`;

const HorizontalWorkflow = ({ 
  mode,
  uploadStatus,
  processingStatus,
  rulesStatus,
  onDownload
}) => {
  
  const getModeDescription = (mode) => {
    const descriptions = {
      auto: '🤖 AI Auto Processing: Automated data quality analysis with cleansing, profiling, and duplicate detection.',
      rules: '🎯 Rules AI Only: Apply custom filtering rules to your raw data using natural language.',
      both: '⚡ AI Auto + Rules: Complete workflow with automated processing followed by optional rule-based filtering.'
    };
    return descriptions[mode] || '';
  };

  // Define workflow steps based on mode
  const getSteps = () => {
    const baseSteps = [
      {
        id: 1,
        icon: uploadStatus === 'loading' ? FiLoader : uploadStatus === 'completed' ? FiCheck : FiUpload,
        title: 'Upload Files',
        description: 'Upload data files',
        status: uploadStatus,
        clickable: false
      }
    ];

    if (mode === 'rules') {
      // Rules only mode: Upload → Rules → Download
      return [
        ...baseSteps,
        {
          id: 2,
          icon: rulesStatus === 'loading' ? FiLoader : rulesStatus === 'completed' ? FiCheck : FiFilter,
          title: 'Apply Rules',
          description: 'Filter with AI',
          status: rulesStatus,
          clickable: false
        }
      ];
    } else if (mode === 'auto') {
      // Auto only mode: Upload → Process → Download
      return [
        ...baseSteps,
        {
          id: 2,
          icon: processingStatus === 'loading' ? FiLoader : processingStatus === 'completed' ? FiCheck : FiCpu,
          title: 'AI Processing',
          description: 'Analyze & validate',
          status: processingStatus,
          clickable: false
        }
      ];
    } else {
      // Both mode: Upload → Process → Rules → Download
      return [
        ...baseSteps,
        {
          id: 2,
          icon: processingStatus === 'loading' ? FiLoader : processingStatus === 'completed' ? FiCheck : FiCpu,
          title: 'AI Processing',
          description: 'Analyze & validate',
          status: processingStatus,
          clickable: false
        },
        {
          id: 3,
          icon: rulesStatus === 'loading' ? FiLoader : rulesStatus === 'completed' ? FiCheck : FiFilter,
          title: 'Apply Rules',
          description: 'Filter (optional)',
          status: rulesStatus,
          clickable: false
        }
      ];
    }
  };

  const steps = getSteps();

  const getStatusText = (status) => {
    if (status === 'completed') return '✓ Done';
    if (status === 'loading') return 'Running...';
    if (status === 'active') return 'Ready';
    return 'Pending';
  };

  // Determine if download should be enabled
  const isDownloadEnabled = () => {
    if (mode === 'rules') {
      return rulesStatus === 'completed';
    } else if (mode === 'auto') {
      return processingStatus === 'completed';
    } else if (mode === 'both') {
      return processingStatus === 'completed'; // Can download after processing or after rules
    }
    return false;
  };

  return (
    <WorkflowContainer>
      {/* Mode Description */}
      {mode && (
        <ModeDescription>
          <strong>{getModeDescription(mode)}</strong>
        </ModeDescription>
      )}

      {/* Workflow Steps */}
      <StepsRow>
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = step.status === 'completed';
          const isLoading = step.status === 'loading';
          const isActive = step.status === 'active';
          const isLast = index === steps.length - 1;
          
          return (
            <React.Fragment key={step.id}>
              <StepWrapper>
                <StepCard
                  $completed={isCompleted}
                  $loading={isLoading}
                  $active={isActive}
                  $clickable={step.clickable}
                  onClick={step.clickable ? step.action : undefined}
                >
                  <StepIconContainer
                    $completed={isCompleted}
                    $loading={isLoading}
                    $active={isActive}
                  >
                    <Icon />
                    <StepNumber 
                      $completed={isCompleted} 
                      $loading={isLoading}
                    >
                      {step.id}
                    </StepNumber>
                  </StepIconContainer>
                  
                  <StepTitle
                    $completed={isCompleted}
                    $loading={isLoading}
                    $active={isActive}
                  >
                    {step.title}
                  </StepTitle>
                  
                  <StepDescription
                    $completed={isCompleted}
                    $loading={isLoading}
                    $active={isActive}
                  >
                    {step.description}
                  </StepDescription>
                  
                  <StepStatus $status={step.status}>
                    {getStatusText(step.status)}
                  </StepStatus>
                </StepCard>
              </StepWrapper>
              
              {!isLast && (
                <Connector 
                  $completed={isCompleted}
                  $active={steps[index + 1].status !== 'pending'}
                >
                  <ConnectorIcon $active={steps[index + 1].status !== 'pending'}>
                    <FiChevronRight />
                  </ConnectorIcon>
                </Connector>
              )}
            </React.Fragment>
          );
        })}
      </StepsRow>
      
      {/* Big Download Button */}
      {isDownloadEnabled() && (
        <DownloadSection>
          <BigDownloadButton onClick={onDownload}>
            <FiDownload />
            Download Excel Report
          </BigDownloadButton>
        </DownloadSection>
      )}
    </WorkflowContainer>
  );
};

export default HorizontalWorkflow;